import React, { useState, useEffect } from 'react';

const DiagnosticPage = () => {
  const [results, setResults] = useState({
    backendUrl: null,
    backendHealth: null,
    error: null,
    loading: true
  });

  useEffect(() => {
    const runDiagnostics = async () => {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

      try {
        const response = await fetch(`${backendUrl}/api/health`);
        const data = await response.json();

        setResults({
          backendUrl,
          backendHealth: data,
          error: null,
          loading: false
        });
      } catch (error) {
        setResults({
          backendUrl,
          backendHealth: null,
          error: error.message,
          loading: false
        });
      }
    };

    runDiagnostics();
  }, []);

  return (
    <div style={{ padding: '20px', fontFamily: 'monospace', maxWidth: '800px', margin: '0 auto' }}>
      <h1 style={{ color: '#333' }}>🔧 Skincare App Diagnostics</h1>

      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
        <h2>Environment Information</h2>
        <table style={{ width: '100%', marginTop: '10px' }}>
          <tbody>
            <tr>
              <td style={{ fontWeight: 'bold', padding: '5px' }}>User Agent:</td>
              <td style={{ padding: '5px', wordBreak: 'break-all' }}>{navigator.userAgent}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 'bold', padding: '5px' }}>Platform:</td>
              <td style={{ padding: '5px' }}>{navigator.platform}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 'bold', padding: '5px' }}>Online:</td>
              <td style={{ padding: '5px' }}>{navigator.onLine ? '✅ Yes' : '❌ No'}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 'bold', padding: '5px' }}>Screen Size:</td>
              <td style={{ padding: '5px' }}>{window.screen.width} x {window.screen.height}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
        <h2>Backend Configuration</h2>
        {results.loading ? (
          <p>🔄 Testing connection...</p>
        ) : (
          <>
            <table style={{ width: '100%', marginTop: '10px' }}>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 'bold', padding: '5px' }}>Backend URL:</td>
                  <td style={{ padding: '5px', wordBreak: 'break-all' }}>{results.backendUrl}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'bold', padding: '5px' }}>Connection:</td>
                  <td style={{ padding: '5px' }}>
                    {results.error ? (
                      <span style={{ color: 'red' }}>❌ Failed: {results.error}</span>
                    ) : (
                      <span style={{ color: 'green' }}>✅ Connected</span>
                    )}
                  </td>
                </tr>
                {results.backendHealth && (
                  <>
                    <tr>
                      <td style={{ fontWeight: 'bold', padding: '5px' }}>Backend Status:</td>
                      <td style={{ padding: '5px' }}>{results.backendHealth.status}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 'bold', padding: '5px' }}>OpenAI Configured:</td>
                      <td style={{ padding: '5px' }}>
                        {results.backendHealth.openaiConfigured ? '✅ Yes' : '❌ No'}
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>

            {results.error && (
              <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fee', borderRadius: '5px', border: '1px solid #fcc' }}>
                <h3 style={{ margin: '0 0 10px 0' }}>⚠️ Problem Detected</h3>
                <p style={{ margin: '5px 0' }}><strong>Error:</strong> {results.error}</p>
                <p style={{ margin: '5px 0' }}><strong>Likely Cause:</strong></p>
                <ul style={{ marginLeft: '20px' }}>
                  {results.backendUrl.includes('localhost') && (
                    <li>Backend URL is set to localhost, but backend is not running locally or not accessible</li>
                  )}
                  {results.backendUrl.includes('192.168') && (
                    <li>Backend URL is set to local network IP, but you're accessing from outside the network</li>
                  )}
                  {!results.backendUrl.includes('localhost') && !results.backendUrl.includes('192.168') && (
                    <li>Backend server might be down or not deployed correctly</li>
                  )}
                </ul>
                <p style={{ margin: '10px 0 5px 0' }}><strong>Solution:</strong></p>
                <ul style={{ marginLeft: '20px' }}>
                  <li>If using Vercel: Deploy backend and set REACT_APP_BACKEND_URL in Vercel environment variables</li>
                  <li>If testing locally: Make sure backend is running (npm start in backend folder)</li>
                  <li>If testing on mobile (same WiFi): Set backend URL to your computer's IP</li>
                </ul>
              </div>
            )}

            {results.backendHealth && !results.backendHealth.openaiConfigured && (
              <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#ffe', borderRadius: '5px', border: '1px solid #ffc' }}>
                <h3 style={{ margin: '0 0 10px 0' }}>⚠️ OpenAI Not Configured</h3>
                <p>The backend is running but OpenAI API key is not configured. Add OPENAI_API_KEY to your environment variables.</p>
              </div>
            )}

            {results.backendHealth && results.backendHealth.openaiConfigured && (
              <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#efe', borderRadius: '5px', border: '1px solid #cfc' }}>
                <h3 style={{ margin: '0 0 10px 0' }}>✅ Everything Looks Good!</h3>
                <p>Backend is connected and OpenAI is configured. The app should work correctly.</p>
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '8px' }}>
        <h3>📋 Quick Fixes</h3>
        <ol style={{ marginLeft: '20px', lineHeight: '1.8' }}>
          <li><strong>For Vercel Deployment:</strong>
            <ul style={{ marginTop: '5px' }}>
              <li>Deploy backend: <code>cd backend && npx vercel</code></li>
              <li>Set environment variable in Vercel dashboard: <code>REACT_APP_BACKEND_URL</code></li>
              <li>Redeploy frontend</li>
            </ul>
          </li>
          <li><strong>For Local Testing:</strong>
            <ul style={{ marginTop: '5px' }}>
              <li>Start backend: <code>cd backend && npm start</code></li>
              <li>Make sure .env has: <code>REACT_APP_BACKEND_URL=http://localhost:3001</code></li>
            </ul>
          </li>
          <li><strong>For Mobile Testing (Same WiFi):</strong>
            <ul style={{ marginTop: '5px' }}>
              <li>Update .env: <code>REACT_APP_BACKEND_URL=http://YOUR_COMPUTER_IP:3001</code></li>
              <li>Restart frontend</li>
              <li>Access from mobile: <code>http://YOUR_COMPUTER_IP:3000</code></li>
            </ul>
          </li>
        </ol>
      </div>

      <div style={{ marginTop: '30px', textAlign: 'center' }}>
        <a href="/" style={{ padding: '10px 20px', backgroundColor: '#7c3aed', color: 'white', textDecoration: 'none', borderRadius: '8px' }}>
          ← Back to App
        </a>
      </div>
    </div>
  );
};

export default DiagnosticPage;
